import { AxiosError } from "axios";
import { asyncNoop, noop } from "es-toolkit";
import Swal from "sweetalert2";
import type { Ref } from "vue";
import { useFacade } from "../facades";

export class RequestController<Res = never> {
    private successFn: (data: Res) => unknown;
    private failureFn: (message: string) => unknown;
    private doneFn: () => unknown = asyncNoop;
    private downloadable = false;

    constructor(
        private data: Ref<{ status: number; message: string; data: Res }>,
        private executeFn: () => Promise<unknown>,
        downloadable = false,
    ) {
        this.successFn = noop;
        this.failureFn = noop;
        this.downloadable = downloadable;
    }

    public standardHooks() {
        this.successFn = () =>
            Swal.fire({
                icon: "success",
                title: "Berhasil",
            });

        this.failureFn = (message: string) =>
            Swal.fire({
                icon: "error",
                title: "Gagal",
                text: message,
            });

        return this;
    }

    public clearHooks() {
        this.successFn = noop;
        this.failureFn = noop;
        return this;
    }

    public onSuccess(successFn: (data: Res) => unknown) {
        this.successFn = successFn;
        return this;
    }

    public onFailure(failureFn: (message: string) => unknown) {
        this.failureFn = failureFn;
        return this;
    }

    public onDone(doneFn: () => unknown) {
        this.doneFn = doneFn;
        return this;
    }

    public async execute(isLoading?: Ref<boolean>): Promise<Res | undefined> {
        try {
            await this.makeRequest(isLoading);
            this.handleServerErrors();

            const response = this.data.value;
            this.successFn(this.downloadable ? (response as any) : response.data);
            return response.data;
        } catch (e) {
            if (e instanceof AxiosError) {
                if (e.status === 503) {
                    if (!import.meta.env.SSR) {
                        Swal.fire({
                            icon: "warning",
                            title: "PERHATIAN",
                            text: "Server sedang berada dalam mode maintenance.",
                        }).then(() => {
                            const f = useFacade();
                            if (f.profile.kind !== "dev") {
                                f.token.logout();
                            } else {
                                window.location.replace("/dev/maintenance");
                            }
                        });
                    }
                } else if (e.status === 401) {
                    if (!import.meta.env.SSR) {
                        Swal.fire({
                            icon: "error",
                            title: "PERHATIAN",
                            html: "Sesi anda telah habis.<br>Silakan login kembali untuk melanjutkan.",
                        }).then(() => {
                            const f = useFacade();
                            window.location.replace("/signin");
                            f.token.logout();
                        });
                    }
                } else {
                    switch (e.code) {
                        case AxiosError.ERR_NETWORK: // this.failureFn("Gagal terhubung ke internet.");
                        case AxiosError.ETIMEDOUT:
                        case AxiosError.ERR_CANCELED:
                        case AxiosError.ECONNABORTED:
                            this.failureFn("Gagal terhubung ke server.");
                            break;
                        default:
                            {
                                // console.error(e.code);
                                // this.failureFn("Terjadi kesalahan yang tidak terduga.");
                                const error = e as Record<string, any>;
                                console.error(e);
                                console.error(error.response.data.message);
                                this.failureFn(error.response.data.message);
                            }

                            break;
                    }
                }
            } else {
                const error = e as Error;
                console.error(error.message);
                this.failureFn(error.message);
            }
        } finally {
            this.doneFn();
            if (isLoading !== undefined) {
                isLoading.value = false;
            }
        }

        return undefined;
    }

    private async makeRequest(isLoading: Ref<boolean> | undefined) {
        if (isLoading !== undefined) {
            const progress = setTimeout(() => (isLoading.value = true), 250);

            try {
                await this.executeFn();
            } finally {
                clearTimeout(progress);
                isLoading.value = false;
            }
        } else {
            try {
                await this.executeFn();
            } catch (err) {
                if (err instanceof AxiosError && this.downloadable) {
                    let errMessage = "Terjadi Kesalahan";
                    const responseData = err.response?.data;

                    if (responseData instanceof ArrayBuffer) {
                        try {
                            const text = new TextDecoder("utf-8").decode(responseData);
                            const json = JSON.parse(text);

                            errMessage = json.message ?? errMessage;
                        } catch (_) {
                            // Empty action
                        }
                    }

                    throw new Error(errMessage, { cause: err });
                }

                throw err;
            }
        }
    }

    private handleServerErrors() {
        const response = this.data.value;

        if (!import.meta.env.SSR && response.status === 401) {
            window.location.replace("/signin");
            throw new Error(response.message);
        }

        if (response.status !== 1 && !this.downloadable) {
            // Error from server.
            throw new Error(response.message);
        }
    }
}
